// ─────────────────────────────────────────────────────────────────────────────
// LabThree — Guida "Le anagrafiche di filiera"
// Centro assistenza → guida per amministratori, solo piano TRACE.
// Fornitori · Clienti · Ubicazioni.
//
// Schermate RICOSTRUITE (vedi la nota in atomi.js). Le etichette fra virgolette
// sono copiate dal codice.
//
// Riferimenti nel codice, per chi dovrà aggiornarla:
//   Fornitori ....... App.js, GestioneFornitori (CAT_FORNITORE, STATI_QUALIFICA,
//                     BadgeQualifica, funzione scadenzaStato per le soglie)
//   Clienti ......... App.js, GestioneClienti; quick-add nel form bolla di uscita
//   Ubicazioni ...... App.js, GestioneUbicazioni
//   KPI 60 giorni ... backend/routes/tracciabilita.js, dashboard:
//                     data_scadenza_qualifica BETWEEN NOW() AND NOW() + 60 days
//
// Verificato: non esiste NESSUNA rotta DELETE per fornitori, clienti e
// ubicazioni. Non si cancellano, si disattivano. Se un giorno venisse aggiunta
// l'eliminazione, il capitolo 6 va riscritto.
// ─────────────────────────────────────────────────────────────────────────────

import React from 'react';
import LT from '../../ui/tokens';
import I, { Ico } from '../../ui/icons';
import {
  GuidaLayout, H, P, Strong, Code, Tasto, Callout, MockFrame, Schermo,
  SchermoTitolo, FAQ,
} from './atomi';

const SEZIONI = [
  { id: 'a0', label: 'Prima di iniziare' },
  { id: 'a1', label: '1. I fornitori' },
  { id: 'a2', label: '2. La qualifica' },
  { id: 'a3', label: '3. I clienti' },
  { id: 'a4', label: '4. I contatti per il richiamo' },
  { id: 'a5', label: '5. Le ubicazioni' },
  { id: 'a6', label: '6. Niente si cancella' },
  { id: 'a7', label: 'Le trappole' },
  { id: 'a8', label: 'Domande frequenti' },
];

const STATI = [
  ['Non qualificato', 'Non lo avete ancora valutato.', LT.n100, LT.n600],
  ['In valutazione',  'Ci state lavorando: documenti richiesti, risposta attesa.', LT.bluSoft, LT.blu],
  ['Qualificato',     'Valutazione superata. È lo stato in cui dovrebbero stare i fornitori che usate.', LT.verdeSoft, '#166534'],
  ['Sospeso',         'Temporaneamente fermo, per esempio dopo una non conformità.', LT.arancioSoft, '#92400E'],
  ['Revocato',        'Chiuso definitivamente.', LT.rossoSoft, '#B91C1C'],
];

const Chippino = ({ testo, bg, fg, mono }) => (
  <span style={{
    padding: '3px 9px', borderRadius: 999, fontSize: 11, fontWeight: 700,
    background: bg, color: fg, whiteSpace: 'nowrap',
    fontFamily: mono ? LT.mono : LT.sans,
  }}>{testo}</span>
);

export default function AnagraficheFiliera() {
  return (
    <GuidaLayout
      titolo="Le anagrafiche di filiera"
      sottotitolo="Chi vi vende, chi vi compra e dove tenete la roba. Sono tre elenchi da compilare una volta, ma dentro c'è la parte che serve il giorno in cui qualcosa va storto."
      durata="~ 12 minuti"
      destinatario="Per amministratori · piano TRACE"
      sezioni={SEZIONI}
    >

      {/* a0 */}
      <H id="a0">Prima di iniziare</H>

      <P>
        Sono le tre anagrafiche che stanno agli estremi della filiera: i{' '}
        <Strong>fornitori</Strong> all'ingresso, i <Strong>clienti</Strong> all'uscita, e le{' '}
        <Strong>ubicazioni</Strong> in mezzo, cioè i posti dello stabilimento dove il prodotto si
        ferma. Nel menu stanno tutte e tre nel gruppo <Code>Configurazione</Code>.
      </P>

      <Callout kind="success" titolo="Un solo campo obbligatorio, in tutte e tre">
        Per un fornitore e per un cliente serve la <Strong>ragione sociale</Strong>, per
        un'ubicazione il <Strong>nome</Strong>. Tutto il resto si può aggiungere dopo, in
        qualsiasi momento. Non bloccatevi a raccogliere partite IVA e indirizzi il primo giorno:
        mettete i nomi, cominciate a lavorare, completate quando i dati vi passano sotto mano.
      </Callout>

      <P>
        Una cosa però conviene farla bene subito, e non è quella che si aspetta la maggior parte
        delle persone: i <Strong>contatti per il richiamo</Strong>. Ci arriviamo al capitolo 4.
      </P>

      {/* a1 */}
      <H id="a1" cat="temperature">1. I fornitori</H>

      <P>
        Da <Code>Fornitori</Code>, il pulsante <Tasto>Nuovo fornitore</Tasto>. La scheda è divisa
        in tre blocchi: i dati anagrafici, la qualifica, e i contatti per il richiamo.
      </P>
      <P>
        Nei dati anagrafici, oltre alla ragione sociale, c'è la <Strong>Categoria</Strong>, che è
        una scelta fra quattro:
      </P>

      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 7, margin: '12px 0 16px' }}>
        {['MATERIA PRIMA', 'MOCA', 'PACKAGING', 'SERVIZI'].map(c => (
          <Chippino key={c} testo={c} bg={LT.bluSoft} fg={LT.blu} />
        ))}
      </div>

      <P>
        <Strong>MOCA</Strong> sta per materiali e oggetti a contatto con gli alimenti: chi vi
        vende budelli, buste, vasetti, pellicole. È una categoria a sé perché quei materiali hanno
        adempimenti propri, e distinguerli vi serve quando dovete tirare fuori le dichiarazioni di
        conformità.
      </P>

      {/* a2 */}
      <H id="a2" cat="formazione">2. La qualifica</H>

      <P>
        Questa è la parte che rende l'anagrafica fornitori qualcosa di più di una rubrica.
        Qualificare un fornitore significa aver verificato che sia affidabile — certificazioni,
        analisi, questionari, visite — e la qualifica <Strong>scade</Strong>, quindi va rinnovata.
        Tenerne traccia è un adempimento, e dimenticarsene è uno dei rilievi più facili da
        prendere in un audit.
      </P>
      <P>
        Nel blocco "Qualifica fornitore" ci sono lo <Strong>Stato qualifica</Strong>, la{' '}
        <Strong>Data qualifica</Strong>, la <Strong>Scadenza qualifica</Strong>, i contatti del
        referente qualità e un campo note. Gli stati sono cinque:
      </P>

      <div style={{ margin: '16px 0' }}>
        {STATI.map(([nome, cosa, bg, fg]) => (
          <div key={nome} style={{
            display: 'flex', gap: 12, alignItems: 'flex-start',
            padding: '9px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <div style={{ width: 130, flexShrink: 0 }}>
              <Chippino testo={nome} bg={bg} fg={fg} />
            </div>
            <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.5 }}>{cosa}</div>
          </div>
        ))}
      </div>

      <P>
        Se compilate la scadenza, nella lista il programma vi avvisa da solo. Il colore del chip
        cambia in base a quanto manca: verde finché siamo larghi, <Strong>arancione da 180
        giorni</Strong> prima, <Strong>rosso da 60 giorni</Strong> prima, e rosso con la scritta{' '}
        <Strong>"Scaduta"</Strong> quando il termine è passato.
      </P>

      <MockFrame didascalia="La card di un fornitore nella lista. Il chip della qualifica si legge a colpo d'occhio, ed è l'unico posto dove il programma vi ricorda di rinnovarla.">
        <Schermo>
          <SchermoTitolo titolo="Fornitori" sottotitolo="4 fornitori in anagrafica" />
          <div style={{
            background: LT.n0, border: `1px solid ${LT.n150}`, borderRadius: LT.r.lg,
            padding: 13, marginBottom: 8,
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.navy, flex: '1 1 auto' }}>
                Caseificio Fratelli Bianchi
              </div>
              <Chippino testo="MATERIA PRIMA" bg={LT.bluSoft} fg={LT.blu} />
            </div>
            <div style={{ fontSize: 12.5, color: LT.n500, marginTop: 3 }}>
              Via Emilia 44, Modena · 059 123456
            </div>
            <div style={{ display: 'flex', gap: 6, marginTop: 9, flexWrap: 'wrap' }}>
              <Chippino testo="Qualificato" bg={LT.verdeSoft} fg="#166534" />
              <Chippino testo="Qualifica: 12/09/2026 · Scade in 26 gg" bg={LT.rossoSoft} fg="#B91C1C" />
              <Chippino testo="Richiamo: Bianchi 335 7788990" bg={LT.rossoSoft} fg="#B91C1C" />
            </div>
          </div>
        </Schermo>
      </MockFrame>

      <Callout kind="tip" titolo="Il promemoria è nella Dashboard">
        Nella <Code>Dashboard</Code> della Tracciabilità c'è un riquadro{' '}
        <Strong>"Fornitori qualif. scad. (60 gg)"</Strong> che conta quelli in scadenza entro due
        mesi, e cliccandoci si arriva all'elenco. Non arrivano mail né avvisi: se nessuno guarda
        la dashboard, nessuno se ne accorge. Vale la pena farci un giro il primo del mese.
      </Callout>

      {/* a3 */}
      <H id="a3">3. I clienti</H>

      <P>
        Struttura quasi identica: <Tasto>Nuovo cliente</Tasto>, ragione sociale obbligatoria e
        tutto il resto facoltativo. In più del fornitore ha il <Strong>codice fiscale</Strong> e
        un blocco <Strong>"Referente principale"</Strong> con nome, telefono ed email — quello con
        cui parlate per gli ordini, che spesso non è la stessa persona da chiamare in emergenza.
      </P>

      <Callout kind="success" titolo="Un cliente si crea anche al volo">
        Se state compilando una bolla di uscita e il cliente non è in anagrafica, non serve
        interrompere tutto. Accanto al campo Cliente c'è <Tasto>+ Nuovo</Tasto>: scrivete la
        ragione sociale, premete <Tasto>Crea</Tasto>, e il cliente viene creato{' '}
        <Strong>e selezionato</Strong> nella bolla. I dati completi si aggiungono con calma
        un'altra volta.
      </Callout>

      {/* a4 */}
      <H id="a4" cat="pulizie">4. I contatti per il richiamo</H>

      <P>
        Sia i fornitori sia i clienti hanno un blocco intitolato{' '}
        <Strong>"Contatti per ritiro / richiamo prodotti"</Strong>, con tre campi: referente,{' '}
        <Strong>Tel. richiamo (urgente)</Strong> ed email. Sono facoltativi, sono gli ultimi della
        scheda, e sono i primi che tutti saltano.
      </P>
      <P>
        Vale la pena spendere due parole su cosa sono davvero. Il giorno in cui un'analisi torna
        male, o un cliente segnala un problema, la domanda non è "chi è il nostro referente
        commerciale". La domanda è <Strong>chi rispondo al telefono adesso</Strong>, che può
        essere sabato alle otto di sera. Il numero del centralino non serve a niente. Serve il
        cellulare di qualcuno che, se squilla, risponde.
      </P>
      <P>
        Quando c'è, nella lista compare un chip rosso con referente e numero, visibile senza
        aprire la scheda: è pensato esattamente per essere letto di fretta.
      </P>

      <Callout kind="warn" titolo="È l'unico campo che si compila per non usarlo mai">
        Sono cinque minuti per cliente e forse non serviranno. Ma è la differenza fra gestire un
        richiamo in mezz'ora e passare la serata a cercare un numero in una mail del 2024.
        Se dovete scegliere una sola cosa da completare oltre ai nomi, è questa.
      </Callout>

      {/* a5 */}
      <H id="a5">5. Le ubicazioni</H>

      <P>
        Un'ubicazione è un posto fisico dove il prodotto si ferma: la sala di lavorazione, la
        cantina di stagionatura, il magazzino dei confezionati, una cella. Due campi soli:{' '}
        <Strong>Nome</Strong> e <Strong>Descrizione</Strong>.
      </P>
      <P>
        La domanda vera non è come si compilano, ma <Strong>quante farne</Strong>. La risposta
        pratica: tante quante sono le distinzioni che vi servono davvero quando cercate qualcosa.
        Se avete tre celle di stagionatura ma non vi interessa mai sapere in quale sta un lotto,
        una sola ubicazione "Cantina stagionatura" è la scelta giusta. Se invece capita di dover
        andare a prendere fisicamente della merce, distinguerle serve.
      </P>

      <Callout kind="tip" titolo="Meglio poche e vere">
        Dieci ubicazioni che corrispondono a come parlate in azienda valgono più di quaranta
        create per scrupolo. L'operatore deve poterle scegliere in fretta da una tendina, con le
        mani sporche e il telefono in una mano.
      </Callout>

      {/* a6 */}
      <H id="a6">6. Niente si cancella</H>

      <P>
        In nessuna delle tre schermate esiste un pulsante per eliminare. Non è una dimenticanza:
        un fornitore, un cliente o un'ubicazione sono agganciati a documenti e movimenti già
        registrati, e cancellarli aprirebbe dei buchi in una catena che deve restare intera.
      </P>
      <P>
        Quello che si fa invece è <Strong>disattivare</Strong>. In modifica — non in creazione —
        c'è una casella, <Tasto>Fornitore attivo</Tasto> oppure <Tasto>Ubicazione attiva</Tasto>,
        e togliendo la spunta il record <Strong>non compare più nei documenti nuovi</Strong> ma
        resta in tutti quelli vecchi. Nella lista lo vedete più chiaro, con l'etichetta
        "Disattivo".
      </P>

      <Callout kind="warn" titolo="Prima di disattivare un'ubicazione, guarda le giacenze">
        Il programma non controlla se dentro c'è ancora del prodotto: la disattiva e basta. Il
        prodotto non si perde — resta registrato lì e continua a comparire nelle giacenze — ma
        l'ubicazione non è più selezionabile, quindi spostarlo diventa scomodo. Fate un salto su{' '}
        <Code>Giacenze per ubicazione</Code> e svuotatela prima.
      </Callout>

      {/* a7 */}
      <H id="a7">Le trappole</H>

      <div style={{ margin: '16px 0' }}>
        {[
          ['Si può creare due volte lo stesso fornitore',
            'Non c\'è nessun controllo sui nomi doppi. Usa la ricerca in cima alla lista prima di premere "Nuovo": due schede per la stessa ditta dividono lo storico in due.'],
          ['La casella "attivo" non c\'è quando crei',
            'Compare solo in modifica. Un record nuovo nasce sempre attivo: per disattivarlo lo salvi e lo riapri.'],
          ['La qualifica scaduta non blocca niente',
            'Puoi registrare ingressi da un fornitore con la qualifica scaduta, e il programma non fiata. Il chip rosso e il riquadro in Dashboard sono l\'unico avviso: sta a voi guardarli.'],
          ['Disattivare non cancella dallo storico',
            'È la cosa giusta, ma va saputa: un fornitore disattivato resta su tutte le bolle in cui compariva. Serve a non riscrivere il passato.'],
        ].map(([t, d]) => (
          <div key={t} style={{
            display: 'flex', gap: 11, alignItems: 'flex-start',
            padding: '11px 0', borderBottom: `1px solid ${LT.n150}`,
          }}>
            <Ico d={I.alert} size={16} stroke={LT.arancio} style={{ marginTop: 3, flexShrink: 0 }} />
            <div>
              <div style={{ fontSize: 14.5, fontWeight: 700, color: LT.n900 }}>{t}</div>
              <div style={{ fontSize: 13.5, color: LT.n600, lineHeight: 1.55, marginTop: 2 }}>{d}</div>
            </div>
          </div>
        ))}
      </div>

      {/* a8 */}
      <H id="a8">Domande frequenti</H>

      <FAQ q="Devo qualificare tutti i fornitori?">
        Il criterio ragionevole è: chi vi vende qualcosa che finisce nel prodotto o lo tocca —
        materie prime, ingredienti, MOCA — va qualificato. Chi vi vende servizi che non incidono
        sulla sicurezza alimentare pesa molto meno. Nel dubbio inseritelo comunque come fornitore
        e lasciate lo stato su "Non qualificato": almeno è tracciato.
      </FAQ>

      <FAQ q="Un fornitore che non uso più: disattivo o lascio stare?">
        Disattivalo. Sparisce dalle tendine dei documenti nuovi, e la lista resta pulita per chi
        registra un ingresso di fretta. Tutto lo storico rimane dov'è.
      </FAQ>

      <FAQ q="Ho sbagliato a scrivere il nome di un cliente, posso correggerlo?">
        Sì, e la correzione si riflette anche sui documenti già emessi, perché puntano
        all'anagrafica. Se invece hai creato due schede per lo stesso cliente, non si fondono: la
        strada è disattivarne una e continuare con l'altra.
      </FAQ>

      <FAQ q="Le ubicazioni servono anche se non uso le fasi di lavorazione?">
        Servono comunque, perché il prodotto finito deve stare in qualche posto e le giacenze si
        leggono per ubicazione. Anche una sola, chiamata come il vostro magazzino, è meglio di
        nessuna.
      </FAQ>

      <FAQ q="Posso importare le anagrafiche da un file Excel?">
        Non c'è una funzione di importazione: si inseriscono a mano. Se hai centinaia di clienti
        da caricare, scrivici: è il tipo di cosa che si fa una volta e conviene farla noi
        direttamente sul database.
      </FAQ>

      <div style={{
        marginTop: 40, padding: 18, background: LT.bluSoft,
        borderRadius: LT.r.lg, border: `1px solid ${LT.n150}`,
      }}>
        <div style={{ fontSize: 15.5, fontWeight: 700, color: LT.navy, marginBottom: 4 }}>
          E adesso?
        </div>
        <div style={{ fontSize: 14, color: LT.n700, lineHeight: 1.65 }}>
          Inserisci i nomi di fornitori, clienti e ubicazioni — solo i nomi, è questione di
          minuti — e passa a <Strong>ingredienti e prodotti</Strong>. Alle qualifiche e ai
          contatti di richiamo torna con calma: l'importante è che ci siano prima che servano.
        </div>
      </div>

    </GuidaLayout>
  );
}
